import React, { useEffect, useState } from 'react';
import { Search, Eye, PauseCircle, KeyRound, XCircle, Home, ChevronRight } from 'lucide-react';
import api from '../../../api';
import BuyerSearchStatusPeopleModal from './BuyerSearchStatusPeopleModal';

/**
 * Buyer lifecycle breakdown (Monday 13068998532, CG 17 Sep 2026).
 *
 * "61 purchased elsewhere, 27 paused on finance" — the aggregate Christian
 * asked for, from the search status buyers set themselves on the portal or
 * from the link in any of our emails.
 *
 * One row per PERSON, not per record: someone with both a CRM contact and a
 * portal account is counted once (see buyer_lifecycle.status_breakdown).
 * Counts are live totals, so they ignore the page's date range on purpose —
 * a status is a current state, not an event in a window.
 */

// Counts, not dates - the repo's toLocaleString lint rule is about date/times.
const NUM = new Intl.NumberFormat('en-AU');

// Mirrors core/utils/buyer_lifecycle_choices.py, so the board still shows all
// seven statuses (at zero) before anyone has answered or if the call fails.
const EMPTY_STATUSES = [
    { value: 'active', label: 'Actively looking', count: 0 },
    { value: 'watching', label: 'Keeping an eye on the market', count: 0 },
    { value: 'paused', label: 'Paused', count: 0 },
    { value: 'purchased_collings', label: 'Purchased through Collings', count: 0 },
    { value: 'purchased_elsewhere', label: 'Purchased elsewhere', count: 0 },
    { value: 'not_looking', label: 'No longer looking', count: 0 },
    { value: 'rented', label: 'Recently rented', count: 0 },
];

const ICONS = {
    active: Search,
    watching: Eye,
    paused: PauseCircle,
    purchased_collings: KeyRound,
    purchased_elsewhere: KeyRound,
    not_looking: XCircle,
    rented: Home,
};

const BuyerSearchStatusPanel = () => {
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [data, setData] = useState(null);
    // Which number was clicked (CG 24 Sep 2026: "Make the buttons clickable").
    // `null` = closed; otherwise { title, subtitle, query } for the drill-in.
    const [drilldown, setDrilldown] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('api/buyer-portal/buyer-search-status/breakdown/');
                if (!cancelled) setData(res.data?.breakdown || null);
            } catch (error) {
                console.error('Failed to load buyer search status breakdown:', error);
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <div className="h-5 w-48 bg-gray-100 rounded animate-pulse mb-4" />
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-20 rounded-xl bg-gray-50 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    // The panel used to return null whenever nothing had been answered yet, so
    // on the day the feature shipped the User Profiles tab looked broken (CG,
    // 21 Sep 2026: "Cant see the analytics in the user profile tab"). Nothing
    // is retrospective, so an empty board is the normal first state and has to
    // say so rather than disappear.
    const statuses = data?.statuses?.length ? data.statuses : EMPTY_STATUSES;
    const total = data?.total || 0;

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <h3 className="text-lg font-bold text-gray-900">Buyer search status</h3>
                <span className="text-xs text-gray-400">
                    {NUM.format(total)} {total === 1 ? 'buyer has' : 'buyers have'} answered
                </span>
            </div>
            <p className="text-sm text-gray-500 mb-5">
                "Are you still actually looking to buy?" answered by buyers on the portal or
                from the link in our emails. Live totals, not filtered by the date range.
                {total > 0 && ' Click any number to see who they are.'}
            </p>

            {failed && (
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    We could not load the buyer search status just now. Refresh the page to try again.
                </div>
            )}

            {!failed && total === 0 && (
                <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                    No buyer has answered yet. Existing buyers keep their alerts exactly as they
                    are until they answer, so these counts fill in as the question goes out on the
                    portal and in our emails.
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {statuses.map(({ value, label, count }) => {
                    const Icon = ICONS[value] || Search;
                    // A tile with nobody in it has nothing to open, so it stays
                    // a plain box rather than a button that leads to an empty
                    // list — which on day one is every tile.
                    const clickable = count > 0;
                    const Tile = clickable ? 'button' : 'div';
                    return (
                        <Tile
                            key={value}
                            {...(clickable ? {
                                type: 'button',
                                onClick: () => setDrilldown({
                                    title: label,
                                    subtitle: `${NUM.format(count)} ${count === 1 ? 'buyer' : 'buyers'} answered "${label}".`,
                                    query: { status: value },
                                }),
                            } : {})}
                            className={`w-full text-left rounded-xl border border-gray-100 bg-gray-50/60 p-3 ${
                                clickable
                                    ? 'cursor-pointer hover:bg-white hover:border-[#55C5D0]/60 hover:shadow-sm transition-all'
                                    : ''
                            }`}
                        >
                            <div className="flex items-center gap-2 text-gray-500 mb-1">
                                <Icon className="w-4 h-4" />
                                <span className="text-xs font-medium">{label}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-bold text-gray-900">
                                    {NUM.format(count)}
                                </span>
                                {clickable && (
                                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-[#0e7490]">
                                        View
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </span>
                                )}
                            </div>
                        </Tile>
                    );
                })}
            </div>

            {data?.not_looking_reasons?.length > 0 && (
                <div className="mt-5">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Why they stopped looking
                    </h4>
                    <div className="space-y-1.5">
                        {data.not_looking_reasons.map(({ value, label, count }) => {
                            const reasonLabel = label || 'No reason given';
                            return (
                                <button
                                    type="button"
                                    key={value || 'none'}
                                    onClick={() => setDrilldown({
                                        title: reasonLabel,
                                        subtitle: `No longer looking: ${reasonLabel.toLowerCase()}.`,
                                        // Empty string is the real "no reason
                                        // given" bucket, so it is sent rather
                                        // than dropped from the query.
                                        query: { status: 'not_looking', reason: value || '' },
                                    })}
                                    className="w-full flex items-center justify-between gap-3 text-sm rounded-lg px-2 py-1 -mx-2 hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-gray-600 text-left">{reasonLabel}</span>
                                    <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
                                        {NUM.format(count)}
                                        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {data?.property_management_leads > 0 && (
                <button
                    type="button"
                    onClick={() => setDrilldown({
                        title: 'Property management leads',
                        subtitle: 'Buyers who purchased and asked for a hand managing the property.',
                        query: { wants_property_management: 1 },
                    })}
                    className="mt-5 w-full flex items-center gap-2.5 rounded-xl border border-[#55C5D0]/40 bg-[#55C5D0]/10 p-3 text-left hover:bg-[#55C5D0]/20 transition-colors"
                >
                    <Home className="w-4 h-4 text-[#0e7490] shrink-0" />
                    <span className="text-sm text-[#0e7490]">
                        <strong>{NUM.format(data.property_management_leads)}</strong> purchased
                        {' '}{data.property_management_leads === 1 ? 'buyer' : 'buyers'} asked for a hand
                        managing the property. Property management leads to follow up.
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#0e7490] shrink-0 ml-auto" />
                </button>
            )}

            {/* Mounted only once a number has been clicked: the drill-in owns
                a router hook, and the board itself must keep rendering in
                places that have no router around it. */}
            {drilldown && (
                <BuyerSearchStatusPeopleModal
                    isOpen
                    onClose={() => setDrilldown(null)}
                    title={drilldown.title}
                    subtitle={drilldown.subtitle}
                    query={drilldown.query}
                />
            )}
        </div>
    );
};

export default BuyerSearchStatusPanel;
